import { useState, useEffect, useContext, useMemo } from 'react'
import { DesignContext } from 'contexts/DesignContext'
import objectPath from 'object-path'
import { Form, Row, Col } from 'react-bootstrap'
import Label from './Label'
import { getCheckValue, isNumber, updateUiSchema } from '../action/Data'
import { debounceCallback } from 'utility/debounceCallback.util'
import { getStorage } from '../action/Storage'
import { isEmpty } from '../action/condition/State'

const CustomRadio = (props) => {
  let {
    value,
    uischema,
    updateRadioData,
    errors = {},
    label,
    setIsOnChange,
    fieldName,
    fieldKey,
    config,
    data,
    uiKey,
    uiName,
  } = props
  let {
    name,
    elementWidth,
    readonly,
    suffixText,
    controlId,
    samePattern,
    radioWidth,
    key,
    require,
    maxHeight,
    defaultValue,
    defaultChecked,
  } = uischema

  const designContext = useContext(DesignContext)
  const {
    jsonForm,
    elementAction,
    setElementAction,
    isUiChanged,
    setIsUiChanged,
  } = designContext
  const datasourceValue = objectPath.get(designContext, `@datasource`) || {}
  const optionType = uischema.optionType || 'default'
  const isJsonRadio = optionType === 'json'
  const isDatasourceRadio = optionType === 'datasource'
  const jsonItem = uischema.json
  const datasourceItem = uischema.datasource
  const hasJsonRadio = jsonItem?.radioItems
    ? objectPath.has(data, jsonItem?.radioItems)
    : false
  const hasDatasourceRadio = datasourceItem?.radioItems
    ? objectPath.has(datasourceValue, datasourceItem?.radioItems)
    : false

  const options = useMemo(() => {
    if (isJsonRadio && hasJsonRadio) {
      const { radioItems, radioName, radioValue, radioLabel } = jsonItem || {}
      let radioOptions = objectPath.get(data, radioItems) || []
      let options = radioOptions.map((option) => {
        option = { ...option }
        if (radioName) {
          option.radioName = objectPath.get(option, radioName)
        }
        if (radioValue) {
          option.value = objectPath.get(option, radioValue)
        }
        if (radioLabel) {
          option.label = objectPath.get(option, radioLabel)
        }
        return option
      })
      return options
    }
    if (isDatasourceRadio && hasDatasourceRadio) {
      const { radioItems, radioName, radioValue, radioLabel } =
        datasourceItem || {}
      let radioOptions = objectPath.get(datasourceValue, radioItems) || []
      let options = radioOptions.map((option) => {
        option = { ...option }
        if (radioName) {
          option.radioName = objectPath.get(option, radioName)
        }
        if (radioValue) {
          option.value = objectPath.get(option, radioValue)
        }
        if (radioLabel) {
          option.label = objectPath.get(option, radioLabel)
        }
        return option
      })
      return options
    }
    let { options = [] } = uischema
    return options
  }, [
    data,
    datasourceValue,
    hasJsonRadio,
    hasDatasourceRadio,
    isJsonRadio,
    isDatasourceRadio,
    jsonItem,
    datasourceItem,
    uischema,
  ])

  let errorArray = objectPath.get(errors, key) || []
  const { isDynamic = false, dynamicKey, dynamicIndex = 0 } = config

  const isLocked = objectPath.get(elementAction, ['isLockedEdit'])
  let hasEnable = objectPath.has(elementAction, ['isEnable', key])
  let isEnable = objectPath.get(elementAction, ['isEnable', key])
  let isReadOnly = useMemo(() => {
    if (isLocked) return isLocked
    if (hasEnable) {
      return isEnable === false ? true : false
    }
    return uischema.readonly
  }, [isEnable, uischema.readonly])

  let hasRequire = objectPath.has(elementAction, ['isRequire', key])
  let requireMessage = objectPath.get(elementAction, ['isRequire', key])
  let showRequireMessage = hasRequire
    ? typeof requireMessage !== 'boolean'
    : require
  let isRequire = hasRequire || require
  let showRequire = value ? false : isRequire ? true : false

  let hasWarning = objectPath.has(elementAction, ['isWarning', key])
  let warningMessage = objectPath.get(elementAction, ['isWarning', key])
  let showWarningMessage = hasWarning
    ? typeof warningMessage !== 'boolean'
    : warningMessage

  const updateRadioAction = (option = {}) => {
    let { showField, hideField, readonlyField, readonly = false } =
      option.action || {}
    let hasAction =
      showField?.length || hideField?.length || readonlyField?.length

    if (!hasAction) return

    let newElementAction = { ...elementAction }
    let newIsUiChanged = { ...isUiChanged }

    if (showField) {
      showField.forEach(({ key }) => {
        let itemKey = getItemKey('isShow', key, newElementAction)
        objectPath.set(newElementAction, itemKey, true)
        objectPath.set(newIsUiChanged, key, { isRender: true })
      })
    }
    if (hideField) {
      hideField.forEach(({ key }) => {
        let itemKey = getItemKey('isShow', key, newElementAction)
        objectPath.set(newElementAction, itemKey, false)
        objectPath.set(newIsUiChanged, key, { isRender: true })
      })
    }
    if (readonlyField) {
      readonlyField.forEach(({ key }) => {
        let itemKey = getItemKey('isEnable', key, newElementAction)
        objectPath.set(newElementAction, itemKey, !readonly)
        objectPath.set(newIsUiChanged, key, { isRender: true })
      })
    }
    setElementAction(newElementAction)
    setIsUiChanged(newIsUiChanged)
  }

  const updateRadioListWithRadioName = (radioName, value) => {
    options.forEach((option) => {
      if (option.radioName === radioName) {
        updateRadioData(value, option.radioName, true)
        updateRadioAction(option)
      } else {
        updateRadioData(undefined, option.radioName, true)
      }
    })
  }
  const getCompareValue = (value1, value2) => {
    value1 = isNumber(value1) ? Number(value1) : value1
    value2 = isNumber(value2) ? Number(value2) : value2
    return value1 === value2
  }

  const radioValue = objectPath.get(data, uischema.key)
  const hasRadioName = options.some(({ radioName }) => radioName)
  const radioNameValue = hasRadioName
    ? JSON.stringify(
        options
          .filter(({ radioName }) => radioName)
          .map(({ radioName }) => objectPath.get(data, radioName)),
      )
    : objectPath.get(data, uiName)
  const hasRadioNameValue = () =>
    options.some(
      ({ radioName }) =>
        radioName && !isEmpty(objectPath.get(data, radioName)),
    )

  const getItemKey = (field, key, actionData = elementAction) => {
    let itemKey
    if (isDynamic) {
      let showElement = objectPath.get(actionData, [field, dynamicKey])
      if (!Array.isArray(showElement)) {
        objectPath.set(actionData, [field, dynamicKey], [])
      }
      itemKey = [field, dynamicKey, dynamicIndex, key].join('.')
    } else {
      itemKey = [field, key].join('.')
    }
    return itemKey
  }

  const checkCheckRadio = (radio, isCheckDefault = false) => {
    let { radioName, value } = radio
    let checked
    let radioValue
    let name = radioName ?? uiName
    let isSetDefaultValue = false

    if (radioName) {
      radioValue = objectPath.get(data, radioName)
    } else {
      radioValue = props.value
    }

    if (isCheckDefault) {
      if (isEmpty(radioValue) && (!radioName || !hasRadioNameValue())) {
        let storage = getStorage(jsonForm, name)
        let defaultRadioValue
        if (isEmpty(storage)) {
          if (defaultChecked) {
            if (objectPath.get(defaultChecked, 'radioName') === radioName) {
              defaultRadioValue = defaultChecked.value
            }
          } else {
            defaultRadioValue = defaultValue
          }
        } else {
          defaultRadioValue = storage
        }
        radioValue = getCheckValue(defaultRadioValue, uischema.format)
        isSetDefaultValue = !isEmpty(radioValue)
      }
      checked = getCompareValue(radioValue, value)
      if (isSetDefaultValue && checked) {
        updateRadioData(value, name, true)
      }
    } else {
      checked = getCompareValue(radioValue, value)
    }
    return checked
  }

  useEffect(() => {
    let option = options.find((radio) => {
      let checked = checkCheckRadio(radio, true)
      return checked
    })
    if (option?.action) {
      updateRadioAction(option)
    }
  }, [defaultChecked, defaultValue, options, radioNameValue, radioValue, value])

  return (
    <Form.Group className="mb-0" controlId={controlId}>
      <Row className="w-100 mx-0">
        <Label
          {...props}
          uischema={uischema}
          label={label}
          className={false}
          mt={2}
          isRequire={isRequire}
          isEnable={!isReadOnly}
        />
        <Col sm={elementWidth} className={`row w-100 mx-0 mt-0 p-0`}>
          <Row
            className={`mx-0 w-100 mt-0 align-items-center`}
            style={{ maxHeight: maxHeight, overflowY: 'auto' }}
          >
            {options
              .filter((radio) => radio.isShow ?? true)
              .map((radio, index) => {
                let { radioName, value } = radio
                let checked = checkCheckRadio(radio)
                return (
                  <Col
                    sm={samePattern ? radioWidth ?? 'auto' : 12}
                    className={`mb-1 ${
                      radioWidth === 'auto' ? 'pl-0' : 'p-0'
                    }`}
                    key={`radio-${key}-${index + 1}`}
                  >
                    <Form.Check
                      style={{ color: radio?.radioColor ?? '#000' }}
                      type="radio"
                      label={radio.label}
                      name={fieldKey}
                      id={`radio-${fieldKey}-${index + 1}`}
                      checked={checked}
                      onChange={(e) => {
                        value = isNumber(value) ? Number(value) : value
                        if (radioName) {
                          updateRadioListWithRadioName(radioName, value)
                        } else {
                          updateRadioData(value, undefined, true)
                          updateRadioAction(radio)
                        }
                        setIsOnChange(true)
                      }}
                      disabled={isReadOnly || radio.isReadOnly}
                    />
                  </Col>
                )
              })}
          </Row>
        </Col>
        {suffixText && (
          <Col className="d-flex align-items-center mt-1 pr-0" sm="auto">
            <Form.Label className={`m-0 w-100`}>{suffixText}</Form.Label>
          </Col>
        )}
      </Row>
      <ul className="m-0 mt-2">
        {showRequire && showRequireMessage && requireMessage && (
          <li className="text-danger">{requireMessage}</li>
        )}
        {showWarningMessage && warningMessage && (
          <li className="text-danger">{warningMessage}</li>
        )}
        {errorArray.map((error, index) => (
          <li key={`error-${key}-${index + 1}`} className="text-danger">
            {error}
          </li>
        ))}
      </ul>
    </Form.Group>
  )
}

export default CustomRadio
